/**
 * Componente: WahaSessionsTable
 * Tabela comparativa de sessões WAHA vs inboxes locais
 * 
 * @version 1.0.0
 * @created 2026-01-05
 */

import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  RefreshCw, 
  Trash2, 
  MoreHorizontal, 
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Ghost,
  Loader2,
  Eye,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { WahaSession, WahaStatus } from '@/hooks/useWahaMonitor';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface WahaSessionsTableProps {
  sessions: WahaSession[];
  onSyncStatus: (params: { sessionName: string; inboxId: string }) => void;
  onDeleteOrphan: (sessionName: string) => void;
  isSyncing?: boolean;
  isDeleting?: boolean;
}

// Tipos de filtro: Status WAHA + Status Local + Especiais
type FilterStatus = 
  | 'all' 
  // Filtros por Status WAHA
  | 'working' | 'stopped' | 'starting' | 'failed' 
  // Filtros por Status Local
  | 'local_active' | 'local_inactive' | 'local_connecting' | 'local_disconnected'
  // Filtros especiais
  | 'orphan' | 'ghost' | 'mismatch';

/**
 * Retorna badge de status WAHA
 */
function getWahaStatusBadge(status: WahaStatus | null) {
  if (!status) {
    return <Badge variant="outline" className="text-muted-foreground">-</Badge>;
  }
  
  // Configuração de badges por status WAHA
  const config: Record<WahaStatus, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof CheckCircle; className?: string }> = {
    'WORKING': { variant: 'outline', icon: CheckCircle, className: 'border-green-500 text-green-500 bg-green-500/10' },
    'STOPPED': { variant: 'destructive', icon: XCircle },
    'STARTING': { variant: 'secondary', icon: Loader2 },
    'SCAN_QR_CODE': { variant: 'secondary', icon: Loader2 },
    'FAILED': { variant: 'destructive', icon: AlertTriangle },
  };
  
  const { variant, icon: Icon, className } = config[status];
  
  return (
    <Badge variant={variant} className={`gap-1 ${className || ''}`}>
      <Icon className="h-3 w-3" />
      {status}
    </Badge>
  );
}

/**
 * Retorna badge de status local
 */
function getLocalStatusBadge(status: string | null, match: boolean) {
  if (!status) {
    return <Badge variant="outline" className="text-muted-foreground">-</Badge>;
  }
  
  return (
    <div className="flex items-center gap-2">
      <Badge 
        variant={match ? 'outline' : 'destructive'}
        className={match ? 'border-green-500/50 text-green-500' : ''}
      >
        {status}
      </Badge>
      {match ? (
        <CheckCircle className="h-4 w-4 text-green-500" />
      ) : (
        <AlertTriangle className="h-4 w-4 text-yellow-500" />
      )}
    </div>
  );
}

/**
 * Tabela de sessões WAHA com comparação local
 */
export function WahaSessionsTable({
  sessions,
  onSyncStatus,
  onDeleteOrphan,
  isSyncing,
  isDeleting,
}: WahaSessionsTableProps) {
  // Estados de filtro
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  
  // Estado para dialog de confirmação de deleção
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; sessionName: string | null }>({
    open: false,
    sessionName: null,
  });

  // Filtrar sessões
  const filteredSessions = sessions.filter((session) => {
    // Filtro de busca
    const searchLower = search.toLowerCase();
    const matchesSearch = 
      session.sessionName.toLowerCase().includes(searchLower) ||
      session.phone?.toLowerCase().includes(searchLower) ||
      session.pushName?.toLowerCase().includes(searchLower) ||
      session.inbox?.name.toLowerCase().includes(searchLower) ||
      session.inbox?.tenant.toLowerCase().includes(searchLower);
    
    if (!matchesSearch) return false;
    
    // Filtro de status (WAHA, Local ou Especiais)
    switch (statusFilter) {
      // Filtros por Status WAHA
      case 'working':
        return session.wahaStatus === 'WORKING';
      case 'stopped':
        return session.wahaStatus === 'STOPPED';
      case 'starting':
        return session.wahaStatus === 'STARTING' || session.wahaStatus === 'SCAN_QR_CODE';
      case 'failed':
        return session.wahaStatus === 'FAILED';
      // Filtros por Status Local
      case 'local_active':
        return session.localStatus === 'active';
      case 'local_inactive':
        return session.localStatus === 'inactive';
      case 'local_connecting':
        return session.localStatus === 'connecting';
      case 'local_disconnected':
        return session.localStatus === 'disconnected';
      // Filtros especiais
      case 'orphan':
        return session.isOrphan;
      case 'ghost':
        return session.isGhost;
      case 'mismatch':
        return !session.match && !session.isOrphan && !session.isGhost;
      default:
        return true;
    }
  });

  // Handler de confirmação de deleção
  const handleConfirmDelete = () => {
    if (deleteDialog.sessionName) {
      onDeleteOrphan(deleteDialog.sessionName);
    }
    setDeleteDialog({ open: false, sessionName: null });
  };

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por sessão, telefone, nome ou inbox..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as FilterStatus)}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {/* Filtros por Status WAHA */}
            <SelectItem value="working">WAHA: WORKING</SelectItem>
            <SelectItem value="stopped">WAHA: STOPPED</SelectItem>
            <SelectItem value="starting">WAHA: STARTING / QR</SelectItem>
            <SelectItem value="failed">WAHA: FAILED</SelectItem>
            {/* Filtros por Status Local */}
            <SelectItem value="local_active">Local: active</SelectItem>
            <SelectItem value="local_inactive">Local: inactive</SelectItem>
            <SelectItem value="local_connecting">Local: connecting</SelectItem>
            <SelectItem value="local_disconnected">Local: disconnected</SelectItem>
            {/* Filtros especiais */}
            <SelectItem value="orphan">Órfãs</SelectItem>
            <SelectItem value="ghost">Fantasmas</SelectItem>
            <SelectItem value="mismatch">Divergentes</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* 
       * ÁREA DE SCROLL ISOLADA DA TABELA
       * Estrutura: border container (overflow-hidden) > scroll container (overflow-auto) > table (min-width)
       * min-w-0 evita que o flex item estoure o container pai
       * z-index baixo (z-[1]) para não sobrepor sidebar (z-10)
       */}
      <div className="rounded-md border overflow-hidden min-w-0">
        <div className="overflow-auto max-h-[calc(100vh-400px)]">
          {/* 
           * Usando <table> nativo em vez de <Table /> do shadcn
           * O componente Table injeta wrapper com overflow-auto que conflita com nosso controle
           * Mantemos as classes do shadcn para estilo consistente
           */}
          <table className="min-w-[1400px] w-max caption-bottom text-sm">
            {/* Header sticky com z-index baixo para não sobrepor sidebar */}
            <TableHeader className="sticky top-0 bg-background z-[1]">
              <TableRow>
                <TableHead className="min-w-[280px]">Sessão WAHA</TableHead>
                <TableHead className="min-w-[130px]">Status WAHA</TableHead>
                <TableHead className="min-w-[140px]">Status Local</TableHead>
                <TableHead className="min-w-[180px]">Telefone</TableHead>
                <TableHead className="min-w-[280px]">Inbox Vinculada</TableHead>
                <TableHead className="min-w-[160px]">Última Validação</TableHead>
                {/* Coluna sticky right com z-index um pouco maior para ficar acima das outras células */}
                <TableHead className="min-w-[80px] text-right sticky right-0 bg-background z-[2] shadow-[-4px_0_8px_rgba(0,0,0,0.05)]">Ações</TableHead>
              </TableRow>
            </TableHeader>
          <TableBody>
            {filteredSessions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  Nenhuma sessão encontrada com os filtros aplicados.
                </TableCell>
              </TableRow>
            ) : (
              filteredSessions.map((session) => (
                <TableRow 
                  key={session.sessionName}
                  className={
                    session.isGhost 
                      ? 'bg-destructive/5' 
                      : session.isOrphan 
                        ? 'bg-orange-500/5' 
                        : !session.match 
                          ? 'bg-yellow-500/5' 
                          : ''
                  }
                >
                  {/* Sessão WAHA */}
                  <TableCell>
                    <div className="flex flex-col">
                      <code className="text-xs font-mono whitespace-nowrap">
                        {session.sessionName}
                      </code>
                      {session.engine && (
                        <span className="text-xs text-muted-foreground mt-1">
                          Engine: {session.engine}
                        </span>
                      )}
                      {session.isOrphan && (
                        <Badge variant="outline" className="mt-1 w-fit text-orange-500 border-orange-500/50">
                          <Ghost className="h-3 w-3 mr-1" />
                          Órfã
                        </Badge>
                      )}
                      {session.isGhost && (
                        <Badge variant="outline" className="mt-1 w-fit text-destructive border-destructive/50">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Fantasma
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  
                  {/* Status WAHA */}
                  <TableCell>
                    {getWahaStatusBadge(session.wahaStatus)}
                  </TableCell>
                  
                  {/* Status Local */}
                  <TableCell>
                    {getLocalStatusBadge(session.localStatus, session.match)}
                  </TableCell>
                  
                  {/* Telefone */}
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-mono text-sm">{session.phone || '-'}</span>
                      {session.pushName && (
                        <span className="text-xs text-muted-foreground">{session.pushName}</span>
                      )}
                    </div>
                  </TableCell>
                  
                  {/* Inbox */}
                  <TableCell>
                    {session.inbox ? (
                      <div className="flex flex-col">
                        <span className="font-medium">{session.inbox.name}</span>
                        <span className="text-xs text-muted-foreground">{session.inbox.tenant}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  
                  {/* Última Validação */}
                  <TableCell>
                    {session.lastValidatedAt ? (
                      <span className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(session.lastValidatedAt), {
                          addSuffix: true,
                          locale: ptBR,
                        })}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Nunca</span>
                    )}
                  </TableCell>
                  
                  {/* Ações - sticky right com z-index para ficar acima das outras células durante scroll */}
                  <TableCell className="text-right sticky right-0 bg-background z-[1] shadow-[-4px_0_8px_rgba(0,0,0,0.05)]">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {/* Ver detalhes - sempre disponível */}
                        <DropdownMenuItem disabled>
                          <Eye className="mr-2 h-4 w-4" />
                          Ver Detalhes
                        </DropdownMenuItem>
                        
                        {/* Sincronizar - apenas se tem inbox vinculada */}
                        {session.inbox && !session.isGhost && (
                          <DropdownMenuItem
                            onClick={() => onSyncStatus({
                              sessionName: session.sessionName,
                              inboxId: session.inbox!.id,
                            })}
                            disabled={isSyncing}
                          >
                            <RefreshCw className="mr-2 h-4 w-4" />
                            {isSyncing ? 'Sincronizando...' : 'Sincronizar Status'}
                          </DropdownMenuItem>
                        )}
                        
                        {/* Deletar - apenas para órfãs */}
                        {session.isOrphan && (
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setDeleteDialog({ 
                              open: true, 
                              sessionName: session.sessionName 
                            })}
                            disabled={isDeleting}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            {isDeleting ? 'Deletando...' : 'Deletar do WAHA'}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
            </TableBody>
          </table>
        </div>
      </div>

      {/* Dialog de confirmação de deleção */}
      <AlertDialog 
        open={deleteDialog.open} 
        onOpenChange={(open) => setDeleteDialog({ open, sessionName: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deletar Sessão Órfã?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação irá remover a sessão <code className="font-mono bg-muted px-1 rounded">
                {deleteDialog.sessionName}
              </code> do servidor WAHA. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
